
import React from 'react';
import { Crown, Sparkles, Stars, Moon, Route, Camera } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';

interface DashboardHeaderProps {
  isSubscribed: boolean;
  hideTopTabs?: boolean;
  isWorkshopLeader?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ isSubscribed, hideTopTabs = false, isWorkshopLeader = false }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'milkyway';

  const handleTabChange = (value: string) => {
    navigate(`/dashboard?tab=${value}`, { replace: true });
  };

  return (
    <div className={`mb-${isMobile ? '8' : '12'}`}>
      <div className={`flex ${isMobile ? 'flex-col items-center text-center' : 'items-center justify-between'} mb-${isMobile ? '6' : '8'}`}>
        <div className={isMobile ? 'mb-4' : ''}>
          <h1 className={`${isMobile ? 'text-3xl' : 'text-4xl'} font-light text-foreground tracking-tight`}>Planning Hub</h1>
          <p className={`${isMobile ? 'text-base' : 'text-lg'} text-muted-foreground mt-2 font-light`}>Plan your Milky Way, Moon, and multi-stop photography trips</p>
        </div>
        
        <div className={`flex items-center gap-3 ${isMobile ? 'flex-col' : ''}`}>
          {isWorkshopLeader && (
            <Link to="/workshops/manage">
              <Button size="sm" className="gap-2 rounded-[4px] bg-[#0f1923] text-white hover:bg-[#1a2b3d]">
                <Camera className="w-4 h-4" />
                Workshop Manager
              </Button>
            </Link>
          )}
          {isSubscribed && (
            <div className={`flex items-center gap-3 px-${isMobile ? '4' : '6'} py-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl shadow-sm backdrop-blur-sm`}>
              <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-amber-800`}>Premium Active</span>
              <Crown className="w-4 h-4 text-amber-600" />
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation - hidden when in detail view */}
      {!hideTopTabs && (
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mb-6">
          <TabsList className={`grid w-full grid-cols-3 ${isMobile ? 'h-14' : 'h-16'} p-1.5 bg-card/95 backdrop-blur-sm border border-border shadow-md rounded-xl`}>
            <TabsTrigger 
              value="milkyway" 
              className={`flex flex-col items-center justify-center gap-0.5 ${isMobile ? 'px-2' : 'px-4'} font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50`}
            >
              <div className="flex items-center gap-2">
                <Stars className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
                  {isMobile ? 'Milky Way' : 'Milky Way Planner'}
                </span>
              </div>
              {!isMobile && (
                <span className="text-xs opacity-80 font-normal">Galactic core visibility</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="moon"
              className={`flex flex-col items-center justify-center gap-0.5 ${isMobile ? 'px-2' : 'px-4'} font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50`}
            >
              <div className="flex items-center gap-2">
                <Moon className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
                  {isMobile ? 'Moon' : 'Moon Planner'}
                </span>
              </div>
              {!isMobile && (
                <span className="text-xs opacity-80 font-normal">Lunar photography timing</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="trips"
              className={`flex flex-col items-center justify-center gap-0.5 ${isMobile ? 'px-2' : 'px-4'} font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50`}
            >
              <div className="flex items-center gap-2">
                <Route className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4'}`} />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
                  {isMobile ? 'Trips' : 'Trip Planner'}
                </span>
              </div>
              {!isMobile && (
                <span className="text-xs opacity-80 font-normal">Multi-stop itineraries</span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
    </div>
  );
};
